import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  pwg208Api,
  FG160,
  FG161,
  FG163,
  FG164,
} from '../../../services/pwg208Api';

const EMPTY_MASTER: FG160 = {
  fg160_01: '',
  fg160_11: null,
  fg160_12: '',
  fg160_13: 1,
  fg160_14: 1,
  fg160_15: '',
  fg160_16: '',
  fg160_17: '',
  fg160_18: '',
  fg160_19: '',
  fg160_20: 1,
  fg160_21: 1,
  fg160_22: 0,
  fg160_221: 0,
  fg160_23: 0,
  fg160_231: 0,
  fg160_24: 'A',
  fg160_25: 0,
  fg160_26: 0,
  fg160_27: 0,
  fg160_28: 0,
  fg160_29: 0,
  fg160_30: '',
  fg160_31: 0,
  fg160_32: '',
  fg160_33: '',
  fg160_80: 'A',
  fg160_81: '',
  fg160_82: '',
  fg160_83: '',
  fg160_91: '',
  fg160_92: null,
  fg160_93: '',
};

export function usePWG208Record() {
  const [record, setRecord] = useState<FG160 | null>(null);
  const [formData, setFormData] = useState<FG160>({ ...EMPTY_MASTER });
  const [details, setDetails] = useState<FG161[]>([]);
  const [prepayments, setPrepayments] = useState<FG163[]>([]);
  const [invoiceItems, setInvoiceItems] = useState<FG164[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pwg208Api.getByID(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details ?? []);
      setPrepayments(data.prepayments ?? []);
      setInvoiceItems(data.invoice_items ?? []);
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ ...EMPTY_MASTER });
    setDetails([]);
    setPrepayments([]);
    setInvoiceItems([]);
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ?? []);
      setPrepayments(record.prepayments ?? []);
      setInvoiceItems(record.invoice_items ?? []);
    } else {
      resetForAdd();
    }
  }, [record, resetForAdd]);

  const getField = useCallback(
    <K extends keyof FG160>(key: K): FG160[K] => formData[key],
    [formData]
  );

  const setField = useCallback(
    <K extends keyof FG160>(key: K, value: FG160[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSave = useCallback(
    async (isAdd: boolean, onSuccess: (saved: FG160) => void) => {
      setLoading(true);
      try {
        const master = { ...formData };
        let saved: FG160;
        if (isAdd) {
          saved = await pwg208Api.create({
            master,
            details,
            prepayments,
            invoice_items: invoiceItems,
          });
        } else {
          saved = await pwg208Api.update(master.fg160_01, {
            master,
            details,
            prepayments,
            invoice_items: invoiceItems,
          });
        }
        setRecord(saved);
        setFormData({ ...saved });
        setDetails(saved.details ?? []);
        setPrepayments(saved.prepayments ?? []);
        setInvoiceItems(saved.invoice_items ?? []);
        message.success('存檔成功');
        onSuccess(saved);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { errors?: string[]; message?: string } } };
        const errors = e?.response?.data?.errors;
        if (errors && errors.length > 0) {
          message.error(errors.join(' / '));
        } else {
          message.error(e?.response?.data?.message ?? '存檔失敗');
        }
      } finally {
        setLoading(false);
      }
    },
    [formData, details, prepayments, invoiceItems]
  );

  const handleDelete = useCallback(
    async (onSuccess: () => void) => {
      if (!record) return;
      setLoading(true);
      try {
        await pwg208Api.delete(record.fg160_01);
        message.success('刪除成功');
        setRecord(null);
        setFormData({ ...EMPTY_MASTER });
        setDetails([]);
        setPrepayments([]);
        setInvoiceItems([]);
        onSuccess();
      } catch {
        message.error('刪除失敗');
      } finally {
        setLoading(false);
      }
    },
    [record]
  );

  // ---- FG161 detail row helpers ----
  const addDetailRow = useCallback(() => {
    setDetails((prev) => {
      const seq = prev.length > 0 ? Math.max(...prev.map((r) => r.fg161_02)) + 1 : 1;
      const newRow: FG161 = {
        fg161_01: formData.fg160_01,
        fg161_02: seq,
        fg161_11: '',
        fg161_12: '',
        fg161_121: '',
        fg161_13: 0,
        fg161_14: 0,
        fg161_15: 0,
        fg161_16: 0,
        fg161_17: 0,
        fg161_18: 0,
        fg161_19: 0,
        fg161_20: 0,
        fg161_21: '',
        fg161_22: '',
        fg161_23: 0,
        fg161_24: '',
        fg161_25: 0,
        fg161_26: '',
        fg161_27: 0,
        fg161_28: 0,
      };
      return [...prev, newRow];
    });
  }, [formData.fg160_01]);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, fg161_02: i + 1 }))
    );
  }, []);

  const updateDetailRow = useCallback(
    <K extends keyof FG161>(idx: number, key: K, value: FG161[K]) => {
      setDetails((prev) =>
        prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
      );
    },
    []
  );

  // ---- FG163 prepayment row helpers ----
  const addPrepaymentRow = useCallback(() => {
    setPrepayments((prev) => {
      const seq = prev.length > 0 ? Math.max(...prev.map((r) => r.fg163_02)) + 1 : 1;
      const newRow: FG163 = {
        fg163_01: formData.fg160_01,
        fg163_02: seq,
        fg163_10: '',
        fg163_11: '',
        fg163_12: 0,
        fg163_13: 0,
      };
      return [...prev, newRow];
    });
  }, [formData.fg160_01]);

  const deletePrepaymentRow = useCallback((idx: number) => {
    setPrepayments((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, fg163_02: i + 1 }))
    );
  }, []);

  const updatePrepaymentRow = useCallback(
    <K extends keyof FG163>(idx: number, key: K, value: FG163[K]) => {
      setPrepayments((prev) =>
        prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
      );
    },
    []
  );

  // ---- FG164 invoice item helpers ----
  const addInvoiceRow = useCallback(() => {
    setInvoiceItems((prev) => {
      const seq = prev.length > 0 ? Math.max(...prev.map((r) => r.fg164_02)) + 1 : 1;
      const newRow: FG164 = {
        fg164_01: formData.fg160_01,
        fg164_02: seq,
        fg164_10: '',
        fg164_11: null,
        fg164_12: 0,
        fg164_13: 0,
        fg164_91: '',
        fg164_92: null,
      };
      return [...prev, newRow];
    });
  }, [formData.fg160_01]);

  const deleteInvoiceRow = useCallback((idx: number) => {
    setInvoiceItems((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, fg164_02: i + 1 }))
    );
  }, []);

  const updateInvoiceRow = useCallback(
    <K extends keyof FG164>(idx: number, key: K, value: FG164[K]) => {
      setInvoiceItems((prev) =>
        prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
      );
    },
    []
  );

  return {
    record,
    formData,
    details,
    prepayments,
    invoiceItems,
    loading,
    loadRecord,
    resetForAdd,
    revertToRecord,
    getField,
    setField,
    handleSave,
    handleDelete,
    // FG161
    addDetailRow,
    deleteDetailRow,
    updateDetailRow,
    // FG163
    addPrepaymentRow,
    deletePrepaymentRow,
    updatePrepaymentRow,
    // FG164
    addInvoiceRow,
    deleteInvoiceRow,
    updateInvoiceRow,
  };
}
